import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminCmsPanel from "@/components/admin/AdminCmsPanel";
import AdminGuideManager from "@/components/admin/AdminGuideManager";
import AdminAnalyticsPanel from "@/components/admin/AdminAnalyticsPanel";
import AdminStats from "@/components/admin/AdminStats";
import AdminSignupsChart from "@/components/admin/AdminSignupsChart";
import AdminUsersTable from "@/components/admin/AdminUsersTable";
import AdminAuditLog from "@/components/admin/AdminAuditLog";
import AdminSystemHealth from "@/components/admin/AdminSystemHealth";
import AdminSupportInbox from "@/components/admin/AdminSupportInbox";

const EMPTY_AUDIT_FILTERS = {
  action: "all",
  actor_id: "all",
  target_id: "all",
  from_date: "",
  to_date: "",
};

export default function Admin() {
  const { user: me } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [audit, setAudit] = useState([]);
  const [auditFilters, setAuditFilters] = useState(EMPTY_AUDIT_FILTERS);

  useEffect(() => {
    if (me && !me.is_admin) navigate("/app", { replace: true });
  }, [me, navigate]);

  const loadAudit = async () => {
    const params = { limit: 100 };
    if (auditFilters.action !== "all") params.action = auditFilters.action;
    if (auditFilters.actor_id !== "all") params.actor_id = auditFilters.actor_id;
    if (auditFilters.target_id !== "all") params.target_id = auditFilters.target_id;
    if (auditFilters.from_date) params.from_date = auditFilters.from_date;
    if (auditFilters.to_date) params.to_date = auditFilters.to_date;
    const { data } = await api.get("/admin/audit", { params });
    setAudit(data);
  };

  const load = async () => {
    if (!me?.is_admin) return;
    const [s, u] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/users", { params: q ? { q } : {} }),
    ]);
    setStats(s.data);
    setUsers(u.data);
    await loadAudit();
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useEffect(() => { if (me?.is_admin) loadAudit(); /* eslint-disable-next-line */ }, [auditFilters]);

  // Deep-link scroll: if the URL has a hash like #admin-users, jump to it once
  // data has been rendered. We wait two frames to give the section a chance to mount.
  useEffect(() => {
    if (!location.hash || !users.length) return;
    const id = location.hash.replace(/^#/, "");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
  }, [location.hash, users.length]);

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-700">Operations</div>
        <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight mt-2">Admin</h1>
        <p className="text-sm text-stone-500 mt-2">Signed in as {me?.email} · platform overview &amp; account controls.</p>
      </div>

      <section id="admin-support"><AdminSupportInbox /></section>
      <section id="admin-stats"><AdminStats stats={stats} /></section>
      <section id="admin-analytics"><AdminAnalyticsPanel /></section>
      <section id="admin-health"><AdminSystemHealth /></section>
      <AdminSignupsChart stats={stats} />
      <section id="admin-users"><AdminUsersTable users={users} q={q} setQ={setQ} onReload={load} /></section>
      <section id="admin-audit"><AdminAuditLog entries={audit} users={users} filters={auditFilters} setFilters={setAuditFilters} /></section>
      <section id="admin-guide" data-testid="admin-guide-section">
        <h2 className="font-display text-2xl font-medium tracking-tight">Beekeeping guide</h2>
        <p className="text-sm text-stone-500 mt-1">Create, edit and reorder the in-app guide articles.</p>
        <AdminGuideManager />
      </section>
      <section id="admin-cms"><AdminCmsPanel /></section>
    </div>
  );
}
